/**
 * useHowlerPlayer - Custom React Hook for Audio Playback
 *
 * A simple, React-idiomatic hook that wraps Howler.js functionality.
 * Delegates all playback semantics (sound-ID tracking, state machine,
 * duplicate-play prevention) to SingleVoiceController, making this hook
 * a thin adapter between the controller and React state.
 *
 * Features:
 * - Single Howl instance management via SingleVoiceController
 * - Automatic cleanup
 * - Browser autoplay handling
 * - Event-based state updates
 * - Progress tracking
 * - Cross-browser volume control (uses Web Audio on iOS where HTML5 volume doesn't work)
 * - External pause/play event detection (for headphones, media keys, AirPods)
 *
 * @see SingleVoiceController for the Howler.js abstraction layer
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Howl } from 'howler';
import { Logger } from '@/app/utils/logger';
import { Track } from '@/types/MusicPlaylist';
import { shouldUseWebAudioForMusic, isIOSDevice } from '@/app/utils/platform';
import { getPerformanceTimingService } from '@/app/services/performance/PerformanceTimingService';
import { SingleVoiceController, VoiceState } from './SingleVoiceController';

export interface UseHowlerPlayerOptions {
  volume?: number; // 0-100
  html5?: boolean; // Force HTML5 Audio (auto-detected for iOS if not specified)
  onEnd?: (track: Track) => void;
  isMuted?: boolean; // Initial mute state
  /**
   * Determines whether a pause that happened during a hidden-tab transition
   * should be auto-recovered when the page becomes visible again.
   */
  shouldRecoverFromVisibilityPause?: () => boolean;
  /**
   * Callback when browser/OS pauses the audio externally (AirPods removed, media keys, etc.)
   * This is important because Howler.js doesn't propagate these events natively.
   * @see https://github.com/goldfire/howler.js/issues/1345
   */
  onExternalPause?: () => void;
  /**
   * Callback when browser/OS resumes the audio externally (AirPods put back in, media keys, etc.)
   */
  onExternalPlay?: () => void;
}

export interface UseHowlerPlayerReturn {
  // Playback state
  isPlaying: boolean;
  isLoading: boolean;
  hasError: boolean;
  errorMessage: string | null;

  // Track info
  currentTime: number;
  duration: number;

  // Controls
  play: () => Promise<void>;
  pause: () => void;
  stop: () => void;
  setVolume: (volume: number) => void;
  seekTo: (time: number) => void;
  setMuted: (muted: boolean) => void;

  // Loading
  loadTrack: (track: Track) => Promise<void>;
  unload: () => void;

  // Utility
  canPlay: boolean;
  isMuted: boolean;

  // Platform info
  isUsingWebAudio: boolean;

  /**
   * Whether external event detection is active (html5 mode only)
   */
  hasExternalEventDetection: boolean;

  /**
   * Get the underlying HTMLAudioElement (only available in html5 mode)
   * Useful for advanced integrations like audio visualization
   */
  getAudioElement: () => HTMLAudioElement | null;
}

const NATURAL_END_EPSILON_SECONDS = 0.2;
const VISIBILITY_PAUSE_GRACE_MS = 1500;
let hasLoggedIOSHtml5VolumeWarning = false;

export const useHowlerPlayer = (options: UseHowlerPlayerOptions = {}): UseHowlerPlayerReturn => {
  const {
    volume: initialVolume = 80,
    html5: html5Option,
    onEnd,
    isMuted: initialMuted = false,
    shouldRecoverFromVisibilityPause,
    onExternalPause,
    onExternalPlay,
  } = options;

  // Determine whether to use HTML5 or Web Audio based on platform
  // On iOS, we MUST use Web Audio (html5: false) for volume control to work
  const isIOS = isIOSDevice();
  const shouldUseWebAudio = shouldUseWebAudioForMusic();
  const isE2E = import.meta.env.VITE_E2E_SETTINGS === 'true';

  // If html5 option is explicitly set, respect it; otherwise auto-detect
  const html5 = html5Option !== undefined ? html5Option : !shouldUseWebAudio;
  const isUsingWebAudio = !html5;

  useEffect(() => {
    if (!isIOS || !html5) {
      return;
    }

    if (hasLoggedIOSHtml5VolumeWarning) {
      return;
    }

    hasLoggedIOSHtml5VolumeWarning = true;
    Logger.warn(
      'Using HTML5 audio on iOS - volume control will NOT work. Consider using html5:false.',
      'useHowlerPlayer'
    );
  }, [isIOS, html5]);

  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [pendingPlay, setPendingPlay] = useState(false); // Track if user wants to play while loading
  const [isMuted, setIsMutedState] = useState(initialMuted);

  const onEndRef = useRef(onEnd);
  const shouldRecoverFromVisibilityPauseRef = useRef(shouldRecoverFromVisibilityPause);
  const onExternalPauseRef = useRef(onExternalPause);
  const onExternalPlayRef = useRef(onExternalPlay);

  useEffect(() => {
    onEndRef.current = onEnd;
  }, [onEnd]);

  useEffect(() => {
    shouldRecoverFromVisibilityPauseRef.current = shouldRecoverFromVisibilityPause;
  }, [shouldRecoverFromVisibilityPause]);

  useEffect(() => {
    onExternalPauseRef.current = onExternalPause;
  }, [onExternalPause]);

  useEffect(() => {
    onExternalPlayRef.current = onExternalPlay;
  }, [onExternalPlay]);

  // Refs
  const howlRef = useRef<Howl | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const volumeRef = useRef(initialVolume);
  const previousVolumeRef = useRef(initialVolume); // For mute/unmute restoration
  const lastVisibilityPauseAtRef = useRef<number | null>(null);
  const shouldResumeAfterVisibilityPauseRef = useRef(false);

  // Load lifecycle guards
  // iOS Safari can fire late WebAudio decode/load callbacks after unload().
  // We guard state updates so only the most recently requested load can update React state.
  const loadIdRef = useRef(0);
  const pendingLoadRejectRef = useRef<((error: Error) => void) | null>(null);

  // --- SingleVoiceController ---
  // The controller manages sound-ID tracking, state machine, and duplicate prevention.
  // It is created once and persists across the hook's lifetime.
  const controllerRef = useRef<SingleVoiceController | null>(null);

  if (controllerRef.current === null) {
    controllerRef.current = new SingleVoiceController(
      {
        onStateChange: (event) => {
          const playing = event.state === VoiceState.Playing;
          setIsPlaying(playing);

          if (playing) {
            startProgressUpdatesRef.current();
          } else {
            stopProgressUpdatesRef.current();
          }
        },
      },
      html5
    );
  }

  const createAbortError = useCallback((message: string) => {
    const error = new Error(message);
    // Match common abort semantics.
    (error as { name?: string }).name = 'AbortError';
    return error;
  }, []);

  const cancelPendingLoad = useCallback((reason: string) => {
    if (pendingLoadRejectRef.current) {
      const reject = pendingLoadRejectRef.current;
      pendingLoadRejectRef.current = null;
      reject(createAbortError(reason));
    }
  }, [createAbortError]);

  const invalidateLoad = useCallback((reason: string) => {
    loadIdRef.current += 1;
    cancelPendingLoad(reason);
  }, [cancelPendingLoad]);

  const markFirstPlaybackForE2E = useCallback(() => {
    if (!isE2E) return;
    getPerformanceTimingService().markFirstPlaybackStart();
  }, [isE2E]);

  // Progress tracking functions (stored in refs for use in controller callback)
  const startProgressUpdates = useCallback(() => {
    if (progressIntervalRef.current) return;

    progressIntervalRef.current = setInterval(() => {
      if (howlRef.current) {
        const time = howlRef.current.seek();
        if (typeof time === 'number') {
          setCurrentTime(time);
        }
      }
    }, 100); // Update every 100ms for smooth progress
  }, []);

  const stopProgressUpdates = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  // Stable refs for progress functions (used by controller callback initializer)
  const startProgressUpdatesRef = useRef(startProgressUpdates);
  const stopProgressUpdatesRef = useRef(stopProgressUpdates);
  startProgressUpdatesRef.current = startProgressUpdates;
  stopProgressUpdatesRef.current = stopProgressUpdates;

  const controller = controllerRef.current;

  // Cleanup function
  const cleanup = useCallback(() => {
    Logger.debug('Cleaning up useHowlerPlayer', 'useHowlerPlayer');

    stopProgressUpdates();

    if (howlRef.current) {
      // Always stop unconditionally – not only when playing().
      // In Safari, calling unload() on a *loading* (not yet playing) Howl does
      // not reliably release the underlying <audio> element's buffer. The old
      // element can continue to decode and then start playing concurrently with
      // the newly created Howl, producing the duplicate-audio ghost stream.
      // stop() before unload() ensures Safari flushes the buffers in all states.
      howlRef.current.stop();

      // Best-effort detach listeners before unloading.
      howlRef.current.off();
      howlRef.current.unload();
      howlRef.current = null;
    }

    // Detach controller
    controller.detach();

    // Reset state
    setIsPlaying(false);
    setIsLoading(false);
    setHasError(false);
    setErrorMessage(null);
    setCurrentTime(0);
    setDuration(0);
    setPendingPlay(false);
  }, [stopProgressUpdates, controller]);

  // Load a track
  const loadTrack = useCallback(async (track: Track) => {
    Logger.info(`Loading track: ${track.title}`, 'useHowlerPlayer', {
      url: track.audioUrl,
      volume: volumeRef.current,
    });

    // Cancel/ignore any in-flight load & callbacks from the previous track.
    invalidateLoad('Track load superseded');
    const loadId = loadIdRef.current;

    // Clean up existing track
    cleanup();
    setCurrentTrack(track);
    setIsLoading(true);
    setHasError(false);
    setErrorMessage(null);
    // Note: Don't reset pendingPlay here - preserve the user's intent to play

    return new Promise<void>((resolve, reject) => {
      try {
        pendingLoadRejectRef.current = reject;

        const isStale = () => loadIdRef.current !== loadId;

        const howl = new Howl({
          src: [track.audioUrl],
          html5,
          volume: volumeRef.current / 100,
          preload: true,

          onload: () => {
            if (isStale()) {
              Logger.debug('Ignoring stale onload callback', 'useHowlerPlayer', {
                title: track.title,
              });
              return;
            }

            Logger.debug('Track loaded successfully', 'useHowlerPlayer', {
              title: track.title,
              duration: howl.duration(),
            });

            setIsLoading(false);
            setDuration(howl.duration() || 0);
            setCurrentTime(0);
            pendingLoadRejectRef.current = null;
            resolve();
          },

          onloaderror: (_, error) => {
            if (isStale()) {
              Logger.debug('Ignoring stale onloaderror callback', 'useHowlerPlayer', {
                title: track.title,
                error,
              });
              return;
            }

            Logger.error(`Failed to load track: ${error}`, 'useHowlerPlayer', {
              title: track.title,
              error,
            });

            setIsLoading(false);
            setHasError(true);
            setErrorMessage(`Failed to load: ${error}`);
            pendingLoadRejectRef.current = null;
            reject(new Error(`Failed to load track: ${error}`));
          },

          onplay: (soundId) => {
            if (isStale()) return;

            Logger.debug('Track started playing', 'useHowlerPlayer', {
              title: track.title,
              soundId,
            });

            // Mark first playback start for performance tracking
            getPerformanceTimingService().markFirstPlaybackStart();

            // Delegate to controller — it handles sound ID capture + state transition.
            controller.handleHowlPlay(soundId);
          },

          onpause: (soundId) => {
            if (isStale()) return;

            Logger.debug('Track paused', 'useHowlerPlayer', {
              title: track.title,
              soundId,
            });

            // Delegate to controller — it filters non-owned sounds.
            controller.handleHowlPause(soundId);
          },

          onend: (soundId) => {
            if (isStale()) return;

            Logger.debug('Track ended', 'useHowlerPlayer', {
              title: track.title,
              soundId,
            });

            // Delegate to controller — it filters non-owned sounds.
            controller.handleHowlEnd(soundId);
            setCurrentTime(0);

            // Notify consumer after state updates are queued
            onEndRef.current?.(track);
          },

          onplayerror: (_, error) => {
            if (isStale()) return;

            Logger.error(`Playback error: ${error}`, 'useHowlerPlayer', {
              title: track.title,
              error,
            });

            setHasError(true);
            setErrorMessage(`Playback error: ${error}`);
            setIsPlaying(false);
            stopProgressUpdates();
          },
        });

        howlRef.current = howl;
        controller.attach(howl);

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        Logger.error('Exception during track loading', 'useHowlerPlayer', {
          title: track.title,
          error: errorMessage,
        });

        setIsLoading(false);
        setHasError(true);
        setErrorMessage(errorMessage);
        pendingLoadRejectRef.current = null;
        reject(new Error(errorMessage));
      }
    });
  }, [cleanup, html5, invalidateLoad, stopProgressUpdates, controller]);

  /**
   * Get the underlying HTMLAudioElement from Howler (HTML5 mode only).
   * Delegates to the controller, which is the single point of _sounds access.
   */
  const getAudioElement = useCallback((): HTMLAudioElement | null => {
    return controller.getAudioElement();
  }, [controller]);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const markRecentVisibilityPause = () => {
      lastVisibilityPauseAtRef.current = Date.now();
    };

    const resumeFromVisibilityPause = () => {
      if (!shouldResumeAfterVisibilityPauseRef.current) {
        return;
      }

      const shouldRecover = shouldRecoverFromVisibilityPauseRef.current?.() ?? true;
      if (!shouldRecover) {
        shouldResumeAfterVisibilityPauseRef.current = false;
        lastVisibilityPauseAtRef.current = null;
        return;
      }

      const audioElement = getAudioElement();
      shouldResumeAfterVisibilityPauseRef.current = false;
      lastVisibilityPauseAtRef.current = null;

      if (!audioElement || !audioElement.paused) {
        return;
      }

      try {
        controller.play();
      } catch (error) {
        Logger.warn('Failed to resume after hidden-tab pause', 'useHowlerPlayer', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resumeFromVisibilityPause();
        return;
      }

      markRecentVisibilityPause();
    };

    const handlePageHide = () => {
      markRecentVisibilityPause();
    };

    const handlePageShow = () => {
      resumeFromVisibilityPause();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [controller, getAudioElement]);

  // ---------------------------------------------------------------------------
  // Playback controls — thin wrappers around SingleVoiceController
  // ---------------------------------------------------------------------------

  const play = useCallback(async (): Promise<void> => {
    markFirstPlaybackForE2E();

    // If still loading, set pending play flag to auto-play when ready
    if (isLoading) {
      Logger.info('Track loading - will auto-play when ready', 'useHowlerPlayer');
      setPendingPlay(true);
      return;
    }

    if (!howlRef.current) {
      throw new Error('No track loaded');
    }

    if (hasError) {
      Logger.warn('Cannot play: track has error', 'useHowlerPlayer', {
        errorMessage,
      });
      return;
    }

    if (howlRef.current.state() === 'loaded') {
      try {
        controller.play();
      } catch (error) {
        Logger.error('Failed to play track', 'useHowlerPlayer', {
          title: currentTrack?.title,
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    } else {
      Logger.warn('Cannot play: track not ready', 'useHowlerPlayer', {
        state: howlRef.current.state(),
      });
    }
  }, [isLoading, hasError, errorMessage, currentTrack?.title, markFirstPlaybackForE2E, controller]);

  const pause = useCallback(() => {
    if (howlRef.current) {
      Logger.debug('Pausing track', 'useHowlerPlayer', {
        title: currentTrack?.title,
      });
      controller.pause();
    }
  }, [currentTrack?.title, controller]);

  const stop = useCallback(() => {
    if (howlRef.current) {
      Logger.debug('Stopping track', 'useHowlerPlayer', {
        title: currentTrack?.title,
      });
      controller.stop();
      setCurrentTime(0);
    }
  }, [currentTrack?.title, controller]);

  const setVolume = useCallback((newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(100, newVolume));
    volumeRef.current = clampedVolume;

    // Store previous volume for mute/unmute (only if not currently muted and volume > 0)
    if (!isMuted && clampedVolume > 0) {
      previousVolumeRef.current = clampedVolume;
    }

    if (howlRef.current) {
      // If muted, don't actually change the Howl volume
      if (!isMuted) {
        howlRef.current.volume(clampedVolume / 100);
      }
    }
  }, [isMuted]);

  const setMuted = useCallback((muted: boolean) => {
    setIsMutedState(muted);

    if (howlRef.current) {
      if (muted) {
        // Store current volume before muting
        previousVolumeRef.current = volumeRef.current > 0 ? volumeRef.current : previousVolumeRef.current;
        howlRef.current.volume(0);
        // Also use Howler's mute for better compatibility
        howlRef.current.mute(true);
      } else {
        // Restore previous volume
        const restoreVolume = previousVolumeRef.current > 0 ? previousVolumeRef.current : 80;
        volumeRef.current = restoreVolume;
        howlRef.current.volume(restoreVolume / 100);
        howlRef.current.mute(false);
      }
    }

    Logger.debug(`Music ${muted ? 'muted' : 'unmuted'}`, 'useHowlerPlayer', {
      previousVolume: previousVolumeRef.current,
    });
  }, []);

  const seekTo = useCallback((time: number) => {
    if (howlRef.current) {
      const clampedTime = Math.max(0, Math.min(duration, time));
      controller.seek(clampedTime);
      setCurrentTime(clampedTime);
    }
  }, [duration, controller]);

  const unload = useCallback(() => {
    invalidateLoad('Track unloaded');
    cleanup();
  }, [cleanup, invalidateLoad]);

  // Computed values
  const canPlay = !isLoading && !hasError && currentTrack !== null;

  // External event detection is only possible in HTML5 mode
  const hasExternalEventDetection = html5;

  /**
   * External audio event detection (HTML5 mode only)
   *
   * Addresses Howler.js issue #1345 where external pause/play events
   * (from headphones, media keys, AirPods, etc.) don't trigger Howler callbacks.
   * We listen directly on the underlying HTMLAudioElement.
   *
   * Uses controller.isInternalActionPending() to distinguish app-initiated
   * operations from browser/OS-initiated ones (replacing the old timing heuristic).
   */
  useEffect(() => {
    if (!html5 || !howlRef.current) return;

    const audioElement = getAudioElement();
    if (!audioElement) return;

    Logger.debug('Setting up external audio event listeners', 'useHowlerPlayer');

    const handleExternalPause = () => {
      // Ignore if we initiated this action
      if (controller.isInternalActionPending()) {
        controller.clearInternalActionPending();
        return;
      }

      const hasReachedNaturalEnd =
        audioElement.ended ||
        (Number.isFinite(audioElement.duration) &&
          audioElement.duration > 0 &&
          audioElement.currentTime >= audioElement.duration - NATURAL_END_EPSILON_SECONDS);

      if (hasReachedNaturalEnd) {
        Logger.debug('Ignoring pause event caused by natural track end', 'useHowlerPlayer', {
          currentTime: audioElement.currentTime,
          duration: audioElement.duration,
          ended: audioElement.ended,
        });
        return;
      }

      if (!audioElement.paused) return;

      const lastVisibilityPauseAt = lastVisibilityPauseAtRef.current;
      const happenedDuringVisibilityTransition =
        lastVisibilityPauseAt !== null && Date.now() - lastVisibilityPauseAt <= VISIBILITY_PAUSE_GRACE_MS;

      if (happenedDuringVisibilityTransition && controller.isCurrentlyPlaying()) {
        shouldResumeAfterVisibilityPauseRef.current = true;
        controller.syncExternalPause();

        Logger.info('Ignoring pause event during hidden-tab transition', 'useHowlerPlayer', {
          currentTime: audioElement.currentTime,
          visibilityState: document.visibilityState,
        });
        return;
      }

      Logger.info('External pause detected (browser/OS/headphones)', 'useHowlerPlayer', {
        currentTime: audioElement.currentTime,
      });

      // Sync controller state
      controller.syncExternalPause();

      // Notify consumer
      onExternalPauseRef.current?.();
    };

    const handleExternalPlay = () => {
      // Ignore if we initiated this action
      if (controller.isInternalActionPending()) {
        controller.clearInternalActionPending();
        return;
      }

      if (audioElement.paused) return;

      shouldResumeAfterVisibilityPauseRef.current = false;
      lastVisibilityPauseAtRef.current = null;

      Logger.info('External play detected (browser/OS/headphones)', 'useHowlerPlayer', {
        currentTime: audioElement.currentTime,
      });

      // Sync controller state (also recovers sound ID if missing)
      controller.syncExternalPlay();

      // Notify consumer
      onExternalPlayRef.current?.();
    };

    audioElement.addEventListener('pause', handleExternalPause);
    audioElement.addEventListener('play', handleExternalPlay);

    return () => {
      audioElement.removeEventListener('pause', handleExternalPause);
      audioElement.removeEventListener('play', handleExternalPlay);
    };
  }, [html5, canPlay, getAudioElement, controller]);

  /**
   * Phantom sound audit on tab resume (Safari safety net).
   *
   * Safari's Web Audio context suspension can leave orphaned sounds in
   * Howler's internal _sounds[] array. Delegates to controller which is
   * the single point of _sounds access for audit purposes.
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden || !howlRef.current) return;
      controller.auditPhantomSounds();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [controller]);

  // Handle pending play when track finishes loading
  useEffect(() => {
    if (pendingPlay && canPlay && howlRef.current) {
      Logger.info('Auto-playing after load (pending play)', 'useHowlerPlayer', {
        title: currentTrack?.title,
      });
      setPendingPlay(false);
      play().catch((error) => {
        Logger.error('Failed to auto-play after load', 'useHowlerPlayer', {
          title: currentTrack?.title,
          error: error instanceof Error ? error.message : String(error),
        });
      });
    }
  }, [pendingPlay, canPlay, currentTrack?.title, play]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      invalidateLoad('Hook unmounted');
      cleanup();
    };
  }, [cleanup, invalidateLoad]);

  return {
    // Playback state
    isPlaying,
    isLoading,
    hasError,
    errorMessage,

    // Track info
    currentTime,
    duration,

    // Controls
    play,
    pause,
    stop,
    setVolume,
    seekTo,
    setMuted,

    // Loading
    loadTrack,
    unload,

    // Utility
    canPlay,
    isMuted,

    // Platform info
    isUsingWebAudio,

    // External event detection
    hasExternalEventDetection,
    getAudioElement,
  };
};
